// src/pages/admin/AdminProyecto/TabProyecto.js
//
// Tab 1: El proyecto (qué es HolaVeci, cómo funciona, pilares,
// mercado, estado, tecnología, modelo económico, próximos pasos).

import React from 'react';
import {
  Box, Card, CardContent, Typography, Alert, Grid, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText,
  Stack, Chip, Divider,
} from '@mui/material';

const BRAND = { primary: '#2E7D32', dark: '#1B5E20', accent: '#F57C00' };

const Metric = ({ label, value, detail, color = BRAND.primary }) => (
  <Paper elevation={0} sx={{
    p: 2, borderRadius: 2, borderLeft: 4, borderColor: color, bgcolor: '#fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)', height: '100%',
  }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
    {detail && (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        {detail}
      </Typography>
    )}
  </Paper>
);

const H2 = ({ children }) => (
  <Typography variant="h5" sx={{
    color: BRAND.dark, fontWeight: 700, mt: 4, mb: 2, pb: 1,
    borderBottom: 2, borderColor: BRAND.primary,
  }}>
    {children}
  </Typography>
);

const H3 = ({ children, sx }) => (
  <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 600, mt: 3, mb: 1.5, ...sx }}>
    {children}
  </Typography>
);

const TermBox = ({ children }) => (
  <Box sx={{
    bgcolor: '#E1F5FE', borderLeft: 3, borderColor: '#0277BD',
    p: 1.5, my: 1.5, borderRadius: 1, color: '#01579B', fontSize: 14,
  }}>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <List dense sx={{ py: 0 }}>
    {items.map((it, i) => (
      <ListItem key={i} sx={{ py: 0.75, pl: 4, position: 'relative',
        '&::before': { content: '"▸"', position: 'absolute', left: 8, color: BRAND.primary, fontWeight: 'bold' },
        borderBottom: 1, borderColor: '#EEE',
        '&:last-child': { borderBottom: 0 },
      }}>
        <ListItemText
          primary={typeof it === 'string' ? it : it}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    ))}
  </List>
);

export default function TabProyecto() {
  return (
    <Box>
      {/* Card destacada: HolaVeci en una frase */}
      <Card sx={{
        background: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
        borderLeft: 4, borderColor: BRAND.primary, mb: 3,
      }}>
        <CardContent>
          <Typography variant="h5" sx={{ color: BRAND.dark, fontWeight: 700, mb: 1.5 }}>
            HolaVeci en una frase
          </Typography>
          <Typography variant="body1">
            HolaVeci es una aplicación móvil que conecta a los vecinos de un barrio con las tiendas de su cuadra
            (la droguería, la panadería, el fruver, la tienda de la esquina, la miscelánea), preservando lo que hace
            único al comercio barrial colombiano: el fiado, la relación humana, el domiciliario del propio tendero,
            y el margen completo del comerciante sobre sus ventas.
          </Typography>
        </CardContent>
      </Card>

      {/* ¿Cómo funciona? */}
      <H2>¿Cómo funciona?</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          HolaVeci es una <strong>aplicación móvil única con tres experiencias diferenciadas según el rol del usuario</strong>:
          veci (vecino que compra), tendero (comerciante que vende), o domiciliario (encargado de la entrega).
          Cada persona descarga la misma aplicación y accede a la experiencia que le corresponde según su rol.
        </Typography>

        <TermBox>
          <strong>Ventaja arquitectónica:</strong> al ser una sola aplicación con roles diferenciados en lugar de tres apps
          independientes, el desarrollo, mantenimiento, actualizaciones y sincronización entre actores son mucho más eficientes.
        </TermBox>

        <H3>Experiencia del veci (el vecino)</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Cualquier persona del barrio descarga la aplicación y ve todas las tiendas dentro de un
          <strong> radio de ubicación configurable (por defecto 2 km, ajustable hasta 1 km)</strong>. Este radio
          está pensado para conjuntos residenciales, urbanizaciones cerradas y barrios pequeños,
          donde el vecino conoce las tiendas de su entorno inmediato y prefiere comprarles a ellos.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Desde la aplicación el veci elige productos, arma su pedido, decide cómo pagar (efectivo, transferencia digital,
          o pide fiado si el tendero lo tiene habilitado y ya es cliente conocido), y espera la entrega. El veci recibe
          notificaciones push en cada cambio de estado del pedido. Durante la fase de preparación puede
          chatear con el tendero; cuando el pedido está en ruta, el chat cambia y el veci puede hablar directamente
          con el domiciliario para coordinar la entrega.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>Chat abierto entre veci y tienda:</strong> además del chat en el contexto de un pedido, el veci
          puede iniciar una conversación con cualquier tienda disponible en su radio para preguntar por
          productos, disponibilidad, horarios u otros temas, sin necesidad de estar comprando.
        </Typography>

        <H3>Experiencia del tendero</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          El tendero recibe los pedidos en su celular, confirma disponibilidad, prepara el pedido y avisa al domiciliario
          cuando está listo. Puede administrar su catálogo, revisar el historial de cada veci, y ver reportes de sus ventas.
          La aplicación funciona en celulares básicos y en 3G.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>Carga de inventario por voz:</strong> el tendero puede cargar productos e inventario hablándole al celular,
          sin escribir manualmente. La plataforma se moderniza continuamente para adaptarse al ritmo real de una tienda de barrio.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>Fiado configurable:</strong> se ofrece únicamente si el tendero lo activa. Cuando está desactivado, la opción
          de pedir fiado no aparece por ningún lado para el veci. Cada tendero decide individualmente. Cada abono se confirma bilateralmente.
        </Typography>

        <H3>Experiencia del domiciliario</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          El domiciliario es <strong>del propio tendero</strong> (o de un grupo de tenderos asociados), no un empleado
          externo de HolaVeci. Recibe la asignación del pedido, actualiza los estados que se notifican automáticamente
          al tendero y al veci, y cuenta con botón de emergencia georreferenciado por seguridad. HolaVeci no maneja
          flota propia ni asume el costo del transporte.
        </Typography>

        <H3>El panel web administrativo</H3>
        <Typography variant="body2">
          Complementa la aplicación móvil con una plataforma web para la gestión operativa de HolaVeci como empresa:
          gestión de concesionarios, aprobación de nuevos negocios, reportería, gestión de pagos y facturación.
        </Typography>
      </CardContent></Card>

      {/* Los 5 pilares */}
      <H2>Los cinco pilares de HolaVeci</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          HolaVeci está diseñado específicamente para el tendero de barrio colombiano y su realidad económica.
          Estas son las cinco decisiones estructurales que definen el modelo:
        </Typography>

        <H3>1. Cero comisión sobre las ventas del tendero</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Una tienda de barrio opera con márgenes del 8% al 15%. Cualquier comisión sobre venta erosiona su rentabilidad
          hasta hacerla inviable. Por eso HolaVeci cobra en su lugar una <strong>suscripción mensual fija muy baja de $30.000</strong>
          (equivalente al costo de Netflix). Al vecino le cobra $200 por pedido, descontados digitalmente. El tendero recibe la plata
          completa por lo que vende.
        </Typography>

        <H3>2. Fiado con confirmación bilateral</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          El fiado es el activo relacional más valioso del comercio barrial. HolaVeci lo digitaliza sin destruirlo:
          el veci pide fiado desde la app, el tendero acepta o rechaza según su criterio, y cada abono queda registrado
          con confirmación de ambas partes.
        </Typography>

        <H3>3. Concesionarios locales, no operaciones desde una torre</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          La adquisición de tenderos se hace mediante <strong>concesionarios zonales</strong>: personas conocidas
          de cada barrio que reciben una comisión recurrente del 20% sobre la suscripción de cada tendero que ellos
          afilian. Descentraliza la operación y crea una red humana difícil de replicar desde una empresa centralizada.
        </Typography>

        <H3>4. Sin flota propia de domiciliarios</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          HolaVeci no contrata domiciliarios ni asume el costo de flota. Son los mismos que ya trabajan con las tiendas.
          Reduce el costo variable por pedido a casi cero.
        </Typography>

        <H3>5. Nominación comunitaria de tiendas</H3>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Los vecis proponen y candidatizan desde la aplicación las tiendas de su barrio. Cuando una tienda acumula
          un número mínimo de nominaciones, entra en cola de acercamiento comercial. El concesionario llega con evidencia
          de que los vecinos ya la piden — la conversación pasa de "le vendo un servicio" a "sus vecinos me pidieron traerlo".
        </Typography>
        <Typography variant="body2">
          <strong>Reemplazo natural de volantes y publicidad física:</strong> HolaVeci reemplaza los volantes y tarjetas
          magnéticas por una presencia digital permanente donde el veci encuentra la tienda justo cuando la necesita.
        </Typography>
      </CardContent></Card>

      {/* El mercado */}
      <H2>El mercado</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Colombia tiene aproximadamente <strong>450.000 tiendas de barrio</strong> (FENALCO, DANE). Sostiene una parte enorme
          del comercio minorista con adopción digital todavía muy baja. Mercado inicial: <strong>Bucaramanga y área metropolitana</strong>,
          con expansión a Bogotá, Medellín y Cali en meses 13-24. Estrategia <strong>barrio por barrio</strong>.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}><Metric label="Tiendas en Colombia"      value="~450.000"  detail="Fuente: FENALCO, DANE" /></Grid>
          <Grid item xs={12} sm={6} md={3}><Metric label="Tiendas Bucaramanga metro" value="~15.000"   detail="Mercado inicial" /></Grid>
          <Grid item xs={12} sm={6} md={3}><Metric label="TAM anual Colombia"        value="$162.000M" detail="Suscripciones potenciales" /></Grid>
          <Grid item xs={12} sm={6} md={3}><Metric label="Penetración en 3 años"     value="~0.3%"     detail="Meta conservadora" /></Grid>
        </Grid>
      </CardContent></Card>

      {/* Estado del proyecto */}
      <H2>¿En qué estado está el proyecto hoy?</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Más de un año de desarrollo. Todo lo técnico está construido y funcional. Falta el arranque comercial:
          publicación en tiendas, formalización tributaria, primeras afiliaciones en barrios piloto.
        </Typography>

        <H3>Lo que ya está listo</H3>
        <BulletList items={[
          'Aplicación móvil Android unificada con las tres experiencias (veci, tendero, domiciliario).',
          'Sistema de nominación comunitaria — los vecis proponen tiendas.',
          'Radio de ubicación configurable (2 km por defecto, ajustable a 1 km).',
          'Fiado configurable por tendero con confirmación bilateral.',
          'Carga de inventario por voz.',
          'Chat contextual dentro del pedido + chat abierto tienda-veci.',
          'Notificaciones push para cambios de estado y mensajes.',
          'Plataforma web administrativa con gestión de concesionarios y reportes.',
          'Backend .NET Core 8 cloud-native en Google Cloud Run.',
          'PostgreSQL en Supabase con arquitectura multi-tenant.',
          'Integración de pagos con Wompi (tarjetas, PSE, Nequi, transferencias).',
          'Botón de emergencia georreferenciado para el domiciliario.',
          'Documentación estratégica, contrato de concesionarios, flujos técnicos, facturación electrónica.',
          'Marca e identidad visual.',
        ]} />

        <H3>Lo que falta para arrancar</H3>
        <BulletList items={[
          'Publicación en Google Play Store (cuenta developer $130.000)',
          'Legalización tributaria: RUT, facturación electrónica DIAN, formalización SAS',
          'Dominio holaveci.co activo con hosting web',
          'Servidores GCP en configuración de producción',
          'Material físico para primer barrio piloto (afiches, stickers, QR físicos)',
          'App Store queda para fase posterior (iOS no es prioridad inicial)',
        ]} />
      </CardContent></Card>

      {/* Tecnología */}
      <H2>Tecnología</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Arquitectura moderna cloud-native, escalable, económica y mantenible.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: BRAND.primary }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Componente</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Tecnología</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Justificación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['Aplicación móvil',     'React Native',        'Una app con roles diferenciados. Un solo código Android/iOS.'],
              ['Frontend web (admin)', 'React.js',            'Ecosistema maduro, mismo stack que móvil.'],
              ['Backend',              '.NET Core 8',         'Rendimiento alto, tipado fuerte, ecosistema empresarial.'],
              ['Infraestructura',      'Google Cloud Run',    'Serverless, escala automática, cobra por uso real.'],
              ['Base de datos',        'PostgreSQL vía Supabase', 'Confiable, económico, capacidades tiempo real.'],
              ['Pagos',                'Wompi (Colombia)',    'Pasarela local con soporte Nequi, PSE, tarjetas.'],
              ['Firma electrónica',    'DocuSign / equivalente', 'Validez legal completa (Ley 527 de 1999).'],
            ].map(([a, b, c]) => (
              <TableRow key={a}><TableCell><strong>{a}</strong></TableCell><TableCell>{b}</TableCell><TableCell>{c}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Modelo económico */}
      <H2>Modelo económico en una tabla</H2>
      <Card><CardContent>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: BRAND.primary }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Concepto</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['Suscripción mensual del tendero',        '$30.000 COP (con IVA incluido)'],
              ['Tarifa por pedido cobrada al veci',      '$200 COP (con IVA incluido)'],
              ['Comisión recurrente concesionario zonal','20% de la suscripción neta ($5.042 por tendero activo)'],
              ['Bono de activación al concesionario',    '$50.000 por tendero que se mantenga activo 90 días'],
              ['Costo operativo mensual (arranque)',     '$1.800.000/mes (desarrollo $1.5M + infra $300K)'],
              ['Punto de equilibrio operativo',          'Mes 7 (~90 tenderos activos)'],
              ['Revenue proyectado mes 24',              '~$27.000.000/mes'],
            ].map(([a, b]) => (
              <TableRow key={a}><TableCell>{a}</TableCell><TableCell><strong>{b}</strong></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Próximos pasos */}
      <H2>Los próximos pasos</H2>
      <Card><CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>Con el capital de arranque, HolaVeci ejecuta este plan de trabajo:</Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: BRAND.primary }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Mes</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Actividad principal</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Hito esperado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              ['Mes 1',     'Publicación app Play, legalización, GCP, dominio', 'App descargable, empresa lista'],
              ['Mes 2',     'Afiliación manual primeros tenderos (piloto)',     '5-10 tenderos activos'],
              ['Mes 3-4',   'Primer concesionario zonal, afiliación acelerada', '25-40 tenderos, primeras utilidades'],
              ['Mes 5-6',   'Segundo barrio, refinamiento con feedback real',   '60-75 tenderos, revenue ~$2.5M'],
              ['Mes 7',     'Punto de equilibrio operativo',                    '~90 tenderos, se sostiene sin capital externo'],
              ['Mes 12',    'Consolidación en Bucaramanga',                     '~200 tenderos, revenue ~$7.5M'],
              ['Mes 18-24', 'Expansión geográfica a segunda ciudad',            '~500 tenderos, revenue ~$27M'],
            ].map(([a, b, c]) => (
              <TableRow key={a}><TableCell><strong>{a}</strong></TableCell><TableCell>{b}</TableCell><TableCell>{c}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </Box>
  );
}
